/**
 * Audio Cache – Speichert TTS-Audio pro Segment persistent in IndexedDB und im Arbeitsspeicher.
 * Der Cache-Key ist ein SHA-256 über Text und alle Syntheseparameter, damit
 * jede Aufnahme auf jedem Gerät dieselbe, kollisionsfreie Kennung hat.
 */

const DB_NAME = 'TheReaderAudioDB';
const STORE_NAME = 'audio_cache';
// Version 2: neue Cache-Keys (SHA-256 inkl. Tempo, Tonhöhe, Format) –
// Einträge mit alten Keys sind nicht mehr auffindbar und werden verworfen
const DB_VERSION = 2;
const MAX_CACHE_SIZE = 500;

const isIndexedDBSupported = typeof indexedDB !== 'undefined';
let dbPromise = null;

/** @type {Map<string, {audioBlob: Blob, audioUrl: string, timestamp: number}>} */
const memoryCache = new Map();

/**
 * Initializes the Audio Cache IndexedDB
 * @returns {Promise<IDBDatabase|null>}
 */
function initDB() {
  if (!isIndexedDBSupported) return Promise.resolve(null);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      store.createIndex('timestamp', 'timestamp', { unique: false });
    };

    request.onblocked = () => {
      console.warn('Audio-Cache wartet: Ein anderer Reader-Tab nutzt noch die alte Version.');
    };

    request.onsuccess = (event) => {
      const db = event.target.result;
      // Künftige Umstellungen nicht blockieren, wenn dieser Tab offen bleibt
      db.onversionchange = () => db.close();
      resolve(db);
    };

    request.onerror = (event) => {
      console.error('Failed to open Audio Cache IndexedDB:', event.target.error);
      reject(event.target.error);
    };
  });

  return dbPromise;
}

/**
 * @typedef {Object} SynthesisParams
 * @property {string} voiceName
 * @property {string} languageCode
 * @property {number|string} speakingRate
 * @property {number|string} pitch
 * @property {string} [audioEncoding='MP3']
 */

/**
 * Generates the cache key for a text and its synthesis parameters
 * @param {string} text
 * @param {SynthesisParams} params - Extra fields (e.g. the full settings) are ignored
 * @returns {Promise<string>} SHA-256 as hex
 */
export async function generateCacheKey(text, params) {
  const { voiceName, languageCode, speakingRate, pitch, audioEncoding = 'MP3' } = params;
  const input = JSON.stringify([
    text.trim(),
    voiceName,
    languageCode,
    Number(speakingRate),
    Number(pitch),
    audioEncoding,
  ]);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Checks if audio for a segment is cached
 * @param {string} text
 * @param {SynthesisParams} params
 * @returns {Promise<boolean>}
 */
export async function has(text, params) {
  const key = await generateCacheKey(text, params);
  if (memoryCache.has(key)) return true;
  if (!isIndexedDBSupported) return false;

  const db = await initDB();
  if (!db) return false;

  return new Promise((resolve) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getKey(key);

    request.onsuccess = () => {
      resolve(request.result !== undefined);
    };

    request.onerror = () => {
      resolve(false);
    };
  });
}

/**
 * Gets cached audio for a segment
 * @param {string} text
 * @param {SynthesisParams} params
 * @returns {Promise<{audioBlob: Blob, audioUrl: string} | null>}
 */
export async function get(text, params) {
  const key = await generateCacheKey(text, params);
  const memEntry = memoryCache.get(key);
  if (memEntry) {
    memEntry.timestamp = Date.now();
    return { audioBlob: memEntry.audioBlob, audioUrl: memEntry.audioUrl };
  }
  if (!isIndexedDBSupported) return null;

  const db = await initDB();
  if (!db) return null;

  return new Promise((resolve) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onsuccess = () => {
      const entry = request.result;
      if (entry) {
        // Create an Object URL from the Blob
        const audioUrl = URL.createObjectURL(entry.blob);
        memoryCache.set(key, {
          audioBlob: entry.blob,
          audioUrl,
          timestamp: Date.now(),
        });
        // Asynchronously update timestamp in IndexedDB for LRU
        updateTimestamp(key, entry.blob).catch(console.error);
        resolve({ audioBlob: entry.blob, audioUrl });
      } else {
        resolve(null);
      }
    };

    request.onerror = () => {
      resolve(null);
    };
  });
}

/**
 * Lists the keys of all persisted entries (for uploading them to the sync server)
 * @returns {Promise<string[]>}
 */
export async function keys() {
  const db = await initDB();
  if (!db) return [...memoryCache.keys()];
  return new Promise((resolve) => {
    const request = db.transaction([STORE_NAME], 'readonly').objectStore(STORE_NAME).getAllKeys();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve([]);
  });
}

/**
 * Reads the recording stored under a key, without touching its LRU timestamp
 * @param {string} key
 * @returns {Promise<Blob|null>}
 */
export async function blobForKey(key) {
  const memEntry = memoryCache.get(key);
  if (memEntry) return memEntry.audioBlob;
  const db = await initDB();
  if (!db) return null;
  return new Promise((resolve) => {
    const request = db.transaction([STORE_NAME], 'readonly').objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result?.blob ?? null);
    request.onerror = () => resolve(null);
  });
}

/**
 * Updates the timestamp of a cache entry in IndexedDB
 * @param {string} key
 * @param {Blob} blob
 */
async function updateTimestamp(key, blob) {
  const db = await initDB();
  if (!db) return;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put({ key, blob, timestamp: Date.now() });
    request.onsuccess = () => resolve();
    request.onerror = (e) => reject(e.target.error);
  });
}

/**
 * Stores audio in the cache (both memory and IndexedDB)
 * @param {string} text
 * @param {SynthesisParams} params
 * @param {Blob} audioBlob
 * @param {string} audioUrl
 * @returns {Promise<void>}
 */
export async function set(text, params, audioBlob, audioUrl) {
  const key = await generateCacheKey(text, params);
  const now = Date.now();

  // Update memory cache
  memoryCache.set(key, {
    audioBlob,
    audioUrl,
    timestamp: now,
  });

  if (!isIndexedDBSupported) return;

  const db = await initDB();
  if (!db) return;

  // Save to IndexedDB
  await new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put({ key, blob: audioBlob, timestamp: now });
    request.onsuccess = () => resolve();
    request.onerror = (e) => reject(e.target.error);
  });

  // Limit size of IndexedDB cache
  await enforceCacheLimit();
}

/**
 * Enforces the maximum size limit of the IndexedDB cache
 */
async function enforceCacheLimit() {
  const db = await initDB();
  if (!db) return;

  // Get current count
  const count = await new Promise((resolve) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.count();
    request.onsuccess = () => resolve(request.result || 0);
    request.onerror = () => resolve(0);
  });

  if (count <= MAX_CACHE_SIZE) return;

  // Fetch entries sorted by timestamp to evict the oldest
  const toEvictCount = count - MAX_CACHE_SIZE;
  const dbEntries = await new Promise((resolve) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('timestamp');
    const request = index.openCursor();
    const results = [];

    request.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor && results.length < toEvictCount) {
        results.push(cursor.value);
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    request.onerror = () => resolve([]);
  });

  if (dbEntries.length === 0) return;

  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const store = transaction.objectStore(STORE_NAME);

  for (const entry of dbEntries) {
    store.delete(entry.key);
    dropFromMemory(entry.key);
  }

  await new Promise((resolve) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
  });
}

/**
 * Löscht alle Audio-Einträge, die älter als `days` Tage sind.
 * @param {number} days - Aufbewahrungsdauer in Tagen; 0 löscht nichts
 * @returns {Promise<number>} Anzahl der gelöschten Einträge
 */
export async function deleteOlderThan(days) {
  if (!days || days <= 0) return 0;
  if (!isIndexedDBSupported) return 0;

  const db = await initDB();
  if (!db) return 0;

  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;

  return new Promise((resolve) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.index('timestamp').openCursor(IDBKeyRange.upperBound(cutoff, true));
    let deleted = 0;

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;
      dropFromMemory(cursor.value.key);
      cursor.delete();
      deleted++;
      cursor.continue();
    };

    transaction.oncomplete = () => resolve(deleted);
    transaction.onerror = () => resolve(deleted);
  });
}

/**
 * Entfernt einen Eintrag aus dem Arbeitsspeicher-Cache und gibt seine Object-URL frei
 * @param {string} key
 */
function dropFromMemory(key) {
  const entry = memoryCache.get(key);
  if (!entry) return;
  if (entry.audioUrl) URL.revokeObjectURL(entry.audioUrl);
  memoryCache.delete(key);
}

/**
 * Clears all cached audio (memory and IndexedDB)
 * @returns {Promise<void>}
 */
export async function clear() {
  // Clear memory cache URLs
  for (const entry of memoryCache.values()) {
    if (entry.audioUrl) {
      URL.revokeObjectURL(entry.audioUrl);
    }
  }
  memoryCache.clear();

  if (!isIndexedDBSupported) return;

  const db = await initDB();
  if (!db) return;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = (e) => reject(e.target.error);
  });
}

/**
 * Returns the current memory cache size
 * @returns {number}
 */
export function size() {
  return memoryCache.size;
}

/**
 * Returns cache statistics
 * @returns {{size: number, maxSize: number}}
 */
export function stats() {
  return {
    size: memoryCache.size,
    maxSize: MAX_CACHE_SIZE,
  };
}
